import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  GiftIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  BugAntIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../lib/utils';

/**
 * Changelog / What's New.
 *
 * Static entries for now, seeded from real commit dates in this
 * repo — every "shipped on" date corresponds to a real commit or
 * ship day. When the Notion pipeline is ready this page becomes a
 * simple fetch: `const releases = await notion.database('…').query()`
 * and the CHANGELOG constant below becomes the fallback shape.
 *
 * The structure intentionally mirrors what Notion would return:
 * releases[] each with { date, title, entries[] } and each entry
 * has a { kind: 'new' | 'improved' | 'fixed', title, body }. Change
 * the source, not the shape.
 */

type EntryKind = 'new' | 'improved' | 'fixed';

interface Entry {
  kind: EntryKind;
  title: string;
  body?: string;
}

interface Release {
  date: string;
  headline: string;
  entries: Entry[];
}

const KIND_META: Record<EntryKind, { label: string; className: string; icon: typeof SparklesIcon }> = {
  new: {
    label: 'New',
    className: 'bg-primary/10 text-primary border-primary/30',
    icon: SparklesIcon,
  },
  improved: {
    label: 'Improved',
    className: 'bg-blue-500/10 text-blue-600 border-blue-500/30 dark:text-blue-400',
    icon: WrenchScrewdriverIcon,
  },
  fixed: {
    label: 'Fixed',
    className: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-400',
    icon: BugAntIcon,
  },
};

const CHANGELOG: Release[] = [
  {
    date: 'September 15, 2026',
    headline: 'The audit shipment',
    entries: [
      { kind: 'new', title: 'Import a project from a PDF', body: 'Drop an issued portfolio PDF on /portfolio/new and Orbi extracts the title, description, category, location, year, and tags into the upload form.' },
      { kind: 'new', title: 'Save any project as PDF', body: 'A printer button on every project detail page produces a clean single-column portfolio spread — no app chrome. Works on your own projects and anyone else\'s.' },
      { kind: 'new', title: 'Onboarding for new accounts', body: 'A three-step welcome page (practice focus, role, where to start) shows once after signup and never again.' },
      { kind: 'new', title: 'Starter prompts on Orbi', body: 'Six prefilled cards below the composer on the Orbi home — Workflow, Research, Portfolio — so the empty state has a launchpad.' },
      { kind: 'new', title: 'Workflow keyboard shortcuts', body: 'V for Select, H for Pan, C for Comment, ⌘E to export the canvas. Hover any tool for the shortcut chip.' },
      { kind: 'improved', title: 'Discover reads better', body: 'One header on both mobile and desktop, Projects and Drawings tabs on both, a Preview-only chip when your plan clips content.' },
      { kind: 'improved', title: 'Project detail — previous / next', body: 'Two cards at the foot of every project link to the previous and next project in the same category so you can keep reading.' },
      { kind: 'improved', title: 'Modals with real focus trap', body: 'The Workflow pre-flight modal and the Lightbox now trap keyboard focus, restore it on close, and answer Escape properly.' },
      { kind: 'improved', title: 'Lightbox gallery keys', body: 'Arrow keys navigate through a gallery, D triggers download, a small counter announces "3 / 8".' },
      { kind: 'improved', title: 'Sharper AI generation', body: 'Prompts trimmed to fit the model\'s attention window, negation moved off the positive prompt, reference images only cross media when it makes sense. Fewer floor-plan requests come back as 3D renders.' },
      { kind: 'improved', title: 'Loading states from one family', body: 'The five different loading spinners scattered across pages replaced with a single Skeleton primitive.' },
      { kind: 'improved', title: 'Pricing page leads with the artefact', body: 'A live SVG mockup of a Workflow canvas sits above the tier table.' },
      { kind: 'improved', title: 'Errors read like sentences', body: '"Orbi AI is temporarily unavailable" is now "Orbi is quiet right now — try again in a moment." Same for password reset, checkout, sign-in.' },
      { kind: 'improved', title: 'Admin promotion actually promotes', body: 'The role dropdown on the Admin page\'s Users tab writes to users.role and admins are recognised in both worker and frontend from that column.' },
      { kind: 'fixed', title: 'Paid features unlocked for paid users', body: 'A brittle single-field tier check was locking Stripe subscribers whose tier landed on a different column. Every gated feature now reads through one resolver that checks all the places.' },
      { kind: 'fixed', title: 'Keyboard focus is visible again', body: 'Global focus ring returns to every focusable control, replacing the invisible border-recolour that WCAG failed the app on.' },
      { kind: 'fixed', title: 'Bookmarks, uploads and edits authenticate', body: 'A missing header made every write from these pages return unauthenticated. Fixed centrally.' },
    ],
  },
  {
    date: 'September 14, 2026',
    headline: 'Workflow gets memory',
    entries: [
      { kind: 'new', title: 'Save and reopen a workflow', body: 'Every canvas autosaves to a URL you can bookmark; the sidebar\'s Recent list shows saved workflows alongside chats.' },
      { kind: 'new', title: 'Q&A pre-flight for a new workflow', body: 'Opening Workflow from Orbi asks three quick questions (program, scale, context) so the first generation has something to work with.' },
      { kind: 'new', title: 'Reference-node pinning', body: 'Mark any node as Reference and every new top-level generation anchors to its image, so a chain doesn\'t drift.' },
      { kind: 'new', title: 'Per-node refine composer', body: 'Every plan node has a sparkles button for a short refinement ("add balconies, warmer light…") that spawns a same-type variant.' },
      { kind: 'new', title: 'Whole-canvas PNG export', body: 'The download tool in the left toolbar exports the entire canvas as a single PNG, filename set from your project name.' },
      { kind: 'new', title: 'Prompt refiner in the composer', body: 'A sparkles button rewrites your prompt with sharper architectural specificity — with one-click undo.' },
      { kind: 'improved', title: 'Research responses look printed', body: 'Rich Markdown rendering across headings, lists, blockquotes, and a distinct References card at the foot.' },
    ],
  },
  {
    date: 'September 7, 2026',
    headline: 'Orbi, Planverse and Workflow launch',
    entries: [
      { kind: 'new', title: 'Orbi AI', body: 'A conversational assistant for architects — Workflow, Research, and Portfolio modes.' },
      { kind: 'new', title: 'Workflow canvas', body: 'A node-based canvas for chaining architectural drawings — Concept, Floor Plan, Elevation, Section, and more.' },
      { kind: 'new', title: 'Planverse feed', body: 'A public gallery of workflows anyone can browse and duplicate.' },
      { kind: 'improved', title: 'Heroicons across the app', body: 'All icons swapped to a single consistent set.' },
    ],
  },
  {
    date: 'August 24, 2026',
    headline: 'Subscriptions',
    entries: [
      { kind: 'new', title: 'Stripe checkout', body: 'Pro and Enterprise tiers with monthly and annual billing.' },
      { kind: 'improved', title: 'Portfolio creation flow', body: 'Guided setup from the Profile popover.' },
    ],
  },
  {
    date: 'August 23, 2026',
    headline: 'Groundwork',
    entries: [
      { kind: 'new', title: 'AI project descriptions', body: 'Generate a project brief from a short prompt.' },
      { kind: 'improved', title: 'Faster import process', body: 'Bulk project imports run through a streamlined worker route.' },
    ],
  },
];

export default function Changelog() {
  const navigate = useNavigate();

  return (
    <div className="mx-auto max-w-3xl px-6 py-8 md:py-12">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-8 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground"
      >
        <ArrowLeftIcon className="h-3.5 w-3.5" />
        Back
      </button>

      <div className="mb-10 flex items-start gap-4">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <GiftIcon className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            What's new
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Ship notes for Planbase — features, improvements, and fixes as they land.
          </p>
        </div>
      </div>

      <ol className="space-y-12">
        {CHANGELOG.map((release) => (
          <li key={release.date} className="relative">
            <div className="mb-4 flex items-baseline gap-3">
              <time className="font-mono text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {release.date}
              </time>
              <span className="text-lg font-semibold text-foreground">
                {release.headline}
              </span>
            </div>
            <ul className="space-y-3">
              {release.entries.map((entry, i) => {
                const meta = KIND_META[entry.kind];
                const KindIcon = meta.icon;
                return (
                  <li
                    key={i}
                    className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4"
                  >
                    <span
                      className={cn(
                        'mt-0.5 inline-flex flex-shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                        meta.className,
                      )}
                    >
                      <KindIcon className="h-3 w-3" />
                      {meta.label}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">{entry.title}</p>
                      {entry.body && (
                        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                          {entry.body}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ol>

      <p className="mt-14 border-t border-border pt-6 text-xs text-muted-foreground">
        Want to know when something ships? This page updates on every release —
        soon it will pull directly from our Notion release log so entries land
        the moment we publish them.
      </p>
    </div>
  );
}
